import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useUser } from '../useUser';
import { useWhere } from '../useWhere';
import { getTimeParams } from '../../util/Restaurant';
import {
  getAddressesRequest,
  getPhones,
} from '../../redux/legacy/reducers/ducks/legacy/ProfileDuck';
import * as orderActions from '../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import { toggleAddress } from '../../redux/legacy/reducers/ducks/legacy/MainDuck';
import { setSelectedAddress as storeSelectedAddress } from '../../redux/legacy/reducers/ducks/new/Address/AddressReducer';
import { formatCartForApi } from '../../util/Helpers';
import findMatchingAddress from '../../util/addressMatch';

function useSetCart(needToStart) {
  const dispatch = useDispatch();
  const { userData } = useUser();

  const [onStartAlready, setOnStartAlready] = useState(false);
  const [onSetCart, setOnSetCart] = useState(false);
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [allReadyForSetCart, setAllReadyForSetCart] = useState(false);
  const [addressIsFine, setAddressIsFine] = useState(false);
  const [phoneIsFine, setPhoneIsFine] = useState(false);
  const [onAddressCreation, setOnAddressCreation] = useState(false);
  const [addressNeededCount, setAddressNeededCount] = useState(0);
  const { dAddress } = useWhere();

  const {
    cartItems,
    selectedRestaurant,
    type,
    time,
    setCartResponse,
    addressResponse,
    address,
    phones,
    getAddressesResponse,
  } = useSelector(
    ({
      order,
      restaurant,
      profile,
      address: selectedAddress,
      time: selectedTime,
    }) => ({
      cartItems: order.cartItems,
      selectedRestaurant: restaurant.selectedRestaurantId,
      type: order?.type,
      time: selectedTime.selectedTime,
      addressResponse: order?.addressResponse,
      setCartResponse: order.setCartResponse,
      address: selectedAddress.selectedAddress,
      phones: profile.phones,
      getAddressesResponse: profile.getAddressesResponse,
    })
  );

  function setCart() {
    const newCartData = formatCartForApi(cartItems);

    const apiData = {
      client: selectedRestaurant,
      data: {
        mode: type.value,
        ...getTimeParams(time),
        cart: newCartData,
      },
    };

    if (
      type.value === 'delivery' &&
      addressResponse?.orderCase &&
      addressResponse?.address?.id &&
      (!addressResponse.address.userId ||
        addressResponse.address.userId === userData?.id)
    ) {
      apiData.data.address =
        addressResponse?.address?.id || dAddress?.address_id;
    } else if (type.value === 'delivery' && address && address?.address_id) {
      apiData.data.address = address?.address_id || dAddress?.address_id;
    }

    if (type.value === 'delivery' && !apiData.data.address) {
      dispatch(toggleAddress(true));
      setAllReadyForSetCart(false);
      setAddressIsFine(false);
      setOnStartAlready(false);
      setAddressNeededCount((c) => c + 1);
      return;
    }

    dispatch(
      orderActions.setCart({
        payloadCartData: { cart: cartItems },
        apiData,
        userProfile: userData,
        isCheckoutStart: true,
      })
    );

    dispatch(
      orderActions.setAddressResponse({
        ...addressResponse,
        orderCase: false,
      })
    );

    setOnSetCart(true);
  }

  // eslint-disable-next-line no-unused-vars
  const [setCartFailedCount, setSetCartFailedCount] = useState(0);

  useEffect(() => {
    if (onSetCart && setCartResponse) {
      if (setCartResponse?.error) {
        setCheckoutStarted(false);
        setSetCartFailedCount((c) => c + 1);
        setOnStartAlready(false);
      } else {
        setCheckoutStarted(true);
      }
    }
  }, [setCartResponse]);

  useEffect(() => {
    if (
      (address && address?.address_id) ||
      (addressResponse?.orderCase && addressResponse?.address?.id)
    ) {
      setAddressIsFine(true);
    }
  }, [address, addressResponse]);

  useEffect(() => {
    if (userData && address) {
      const hasStaleGuestAddress = address.address_id && !address.user_id;
      const hasMismatchedUser =
        address.user_id && userData.id !== address.user_id;
      if (hasStaleGuestAddress || hasMismatchedUser) {
        // eslint-disable-next-line no-unused-vars, camelcase
        const { user_id, address_id, primary, ...cleanedAddress } = address;
        dispatch(storeSelectedAddress(cleanedAddress));
      }
    }
  }, [userData, address]);

  useEffect(() => {
    if (onAddressCreation) {
      if (addressResponse && addressResponse?.address?.id) {
        const updatedAddress = {
          ...address,
          primary: false,
          user_id: userData.id,
          address_id: addressResponse?.address?.id,
          aftercreated: true,
        };

        dispatch(storeSelectedAddress(updatedAddress));
      } else {
        setOnStartAlready(false);
      }
    }
  }, [addressResponse, onAddressCreation]);

  useEffect(() => {
    if ((phoneIsFine && addressIsFine) || address?.aftercreated) {
      setAllReadyForSetCart(true);
    }
  }, [phoneIsFine, addressIsFine, address, addressResponse]);

  useEffect(() => {
    if (addressResponse?.orderCase && allReadyForSetCart) {
      setCart();
    }
    if (allReadyForSetCart && needToStart) {
      setCart();
    }
  }, [
    allReadyForSetCart,
    needToStart,
    addressResponse?.address?.id,
    address?.address_id,
  ]);

  const [addressesFetchRequested, setAddressesFetchRequested] = useState(false);

  useEffect(() => {
    if (needToStart && !onStartAlready) {
      if (userData) {
        if (type.value === 'delivery' && !address) {
          dispatch(toggleAddress(true));
          setAddressNeededCount((c) => c + 1);
          return;
        }

        const needsAddressResolution =
          type.value === 'delivery' &&
          address &&
          (!address.address_id ||
            (address.user_id && userData.id !== address.user_id)) &&
          !onAddressCreation;

        if (
          needsAddressResolution &&
          !getAddressesResponse?.response &&
          !addressesFetchRequested
        ) {
          setAddressesFetchRequested(true);
          dispatch(getAddressesRequest());
          return;
        }

        setOnStartAlready(true);

        let goSetCart = true;

        if (needsAddressResolution) {
          goSetCart = false;
          setOnAddressCreation(true);
          const exists = findMatchingAddress(
            getAddressesResponse?.response,
            address
          );
          if (!exists) {
            dispatch(
              orderActions.createNewAddress({
                address,
                userId: userData.id,
                orderCase: true,
              })
            );
          } else {
            dispatch(
              orderActions.setAddressResponse({
                address: { ...exists, userId: userData.id },
                orderCase: false,
              })
            );
          }
        } else {
          setAddressIsFine(true);
        }

        // TODO: we should wait until created just like the address
        if (phones?.length === 0) {
          dispatch(getPhones()); // TODO: To be replaced with create phone
        }

        setPhoneIsFine(true);

        if (goSetCart) {
          setAllReadyForSetCart(true);
        }
      }
    }
  }, [needToStart, userData, address, getAddressesResponse]);

  return [checkoutStarted, addressNeededCount, setCartFailedCount];
}

export default useSetCart;
